import axios from 'axios';

/**
 * Creates an authenticated API client that supports both session and JWT auth
 *
 * @param {Object} options
 * @param {string} options.baseURL - Base URL for API requests
 * @param {Function} options.getAccessToken - Async function to get JWT access token (optional)
 * @param {Function} options.onUnauthorized - Callback when 401 received (optional)
 * @returns {axios.AxiosInstance}
 */
export const createApiClient = ({ baseURL, getAccessToken, onUnauthorized } = {}) => {
  const client = axios.create({
    baseURL: baseURL || '/api',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  // Request interceptor - add auth headers
  client.interceptors.request.use(async (config) => {
    // Always add CSRF token for session-based auth
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // If getAccessToken is provided, use JWT auth
    if (getAccessToken) {
      try {
        const token = await getAccessToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('[ApiClient] Error getting access token:', error);
      }
    }

    return config;
  });

  // Response interceptor - handle auth errors
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.log('[ApiClient] 401 Unauthorized');
        if (onUnauthorized) {
          onUnauthorized();
          // Return never-resolving promise to prevent error propagation during redirect
          return new Promise(() => {});
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Default API client using session-based auth
 * Redirects to login on 401
 */
export const defaultApiClient = createApiClient({
  baseURL: '/api',
  onUnauthorized: () => {
    window.location.href = '/login';
  }
});

/**
 * Creates a hook factory for API clients with auth context
 */
export const useApiClient = (baseURL = '/api') => {
  // This is meant to be used with useAuth context
  // For now, return the default client
  // In full JWT mode, this would use getAccessToken from context
  return defaultApiClient;
};

export default createApiClient;
